import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';
import AssignUserModal from './components/AssignUserModal';
import type { TenantUser, Tenant } from './components/AssignUserModal';

// ── Types ─────────────────────────────────────────────────────────────────────
interface SuperAdminRow { id: string; user_id: string; created_at: string; }

type ViewTab = 'assignments' | 'superadmins';

// ── Constants ─────────────────────────────────────────────────────────────────
const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const ROLE_STYLES: Record<string, { bg: string; text: string }> = {
  owner:   { bg: 'bg-amber-50',   text: 'text-amber-700' },
  admin:   { bg: 'bg-blue-50',    text: 'text-blue-700' },
  editor:  { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  member:  { bg: 'bg-slate-100',  text: 'text-slate-500' },
  viewer:  { bg: 'bg-slate-100',  text: 'text-slate-400' },
};

const PLAN_BADGE: Record<string, string> = {
  free:       'bg-slate-100 text-slate-500',
  pro:        'bg-amber-50 text-amber-700',
  enterprise: 'bg-emerald-50 text-emerald-700',
};

const ROLES = ['owner', 'admin', 'editor', 'member', 'viewer'];

// ── Copy helper ───────────────────────────────────────────────────────────────
function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    Swal.fire({ title: 'Copiado', icon: 'success', timer: 900, showConfirmButton: false,
      background: '#ffffff', color: '#0f172a', toast: true, position: 'top-end' });
  });
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function SAUsers() {
  const navigate = useNavigate();

  const [users, setUsers]             = useState<TenantUser[]>([]);
  const [superAdmins, setSuperAdmins] = useState<SuperAdminRow[]>([]);
  const [tenants, setTenants]         = useState<(Tenant & { plan_code?: string })[]>([]);
  const [superAdminIds, setSuperAdminIds] = useState<Set<string>>(new Set());
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [roleFilter, setRoleFilter]   = useState('all');
  const [viewTab, setViewTab]         = useState<ViewTab>('assignments');

  // modal
  const [modalOpen, setModalOpen]     = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [isNew, setIsNew]             = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [usersRes, tenantsRes, saRes] = await Promise.all([
      supabase.from('saas_tenant_users').select('*').order('created_at', { ascending: false }),
      supabase.from('saas_tenants').select('id, name, slug, plan_code'),
      supabase.from('saas_super_admins').select('*').order('created_at', { ascending: false }),
    ]);

    const tenantList = (tenantsRes.data ?? []) as (Tenant & { plan_code: string })[];
    const saList     = (saRes.data ?? []) as SuperAdminRow[];
    const saIds      = new Set(saList.map((r) => r.user_id));
    const tenantMap  = Object.fromEntries(tenantList.map((t) => [t.id, t]));

    const enriched = ((usersRes.data ?? []) as TenantUser[]).map((u) => ({
      ...u,
      tenant_name: tenantMap[u.tenant_id]?.name ?? '—',
      is_super_admin: saIds.has(u.user_id),
    }));

    setUsers(enriched);
    setTenants(tenantList);
    setSuperAdmins(saList);
    setSuperAdminIds(saIds);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      fetchData();
    };
    init();
  }, [navigate, fetchData]);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const uniqueUserIds = new Set(users.map((u) => u.user_id));
  const saOnlyCount   = superAdmins.filter((sa) => !uniqueUserIds.has(sa.user_id)).length;

  const filteredUsers = users.filter((u) => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q
      || u.user_id.toLowerCase().includes(q)
      || (u.tenant_name ?? '').toLowerCase().includes(q);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const filteredSA = superAdmins.filter((sa) => {
    const q = search.trim().toLowerCase();
    return !q || sa.user_id.toLowerCase().includes(q);
  });

  // ── Role change (inline Swal) ─────────────────────────────────────────────────
  const handleChangeRole = async (u: TenantUser) => {
    const opts = ROLES.reduce<Record<string, string>>((acc, r) => {
      acc[r] = r.charAt(0).toUpperCase() + r.slice(1);
      return acc;
    }, {});
    const { value: newRole } = await Swal.fire({
      title: 'Cambiar rol',
      html: `<p style="color:#64748b;font-size:13px">Tenant: <strong>${u.tenant_name}</strong></p>`,
      input: 'select', inputOptions: opts, inputValue: u.role,
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newRole || newRole === u.role) return;
    const { error } = await supabase.from('saas_tenant_users').update({ role: newRole }).eq('id', u.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
    Swal.fire({ title: 'Rol actualizado', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  // ── SA toggle ─────────────────────────────────────────────────────────────────
  const handleToggleSA = async (userId: string, tenantName?: string) => {
    const isSA = superAdminIds.has(userId);
    const { isConfirmed } = await Swal.fire({
      title: `¿${isSA ? 'Quitar' : 'Dar'} Super Admin?`,
      html: `
        <p style="color:#64748b;font-size:13px;margin-bottom:8px">
          Usuario: <code style="color:#2563eb;background:#eff6ff;padding:2px 6px;border-radius:4px;font-size:11px">${userId.slice(0, 20)}…</code>
        </p>
        ${tenantName ? `<p style="color:#64748b;font-size:12px">Tenant: <strong>${tenantName}</strong></p>` : ''}
        <div style="background:${isSA ? '#fef2f2' : '#eff6ff'};border:1px solid ${isSA ? '#fecaca' : '#bfdbfe'};border-radius:8px;padding:8px 10px;margin-top:10px">
          <p style="color:${isSA ? '#b91c1c' : '#1d4ed8'};font-size:12px;margin:0">
            ${isSA ? '⚠️ Se perderá acceso completo al Super Admin.' : '⭐ Obtendrá acceso completo a toda la plataforma.'}
          </p>
        </div>`,
      icon: isSA ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: isSA ? 'Sí, quitar SA' : 'Sí, dar SA',
      cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: isSA ? '#ef4444' : '#7c3aed',
    });
    if (!isConfirmed) return;

    if (isSA) {
      const { error } = await supabase.from('saas_super_admins').delete().eq('user_id', userId);
      if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
      setSuperAdminIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
      setUsers((prev) => prev.map((x) => x.user_id === userId ? { ...x, is_super_admin: false } : x));
      setSuperAdmins((prev) => prev.filter((sa) => sa.user_id !== userId));
      Swal.fire({ title: 'Super Admin removido', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
    } else {
      const { error } = await supabase.from('saas_super_admins').insert({ user_id: userId });
      if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
      setSuperAdminIds((prev) => new Set([...prev, userId]));
      setUsers((prev) => prev.map((x) => x.user_id === userId ? { ...x, is_super_admin: true } : x));
      setSuperAdmins((prev) => [...prev, { id: '', user_id: userId, created_at: new Date().toISOString() }]);
      Swal.fire({ title: '⭐ Super Admin asignado', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
    }
  };

  // ── Remove from tenant ────────────────────────────────────────────────────────
  const handleRemove = async (u: TenantUser) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Remover del tenant?',
      html: `<p style="color:#64748b;font-size:13px">El usuario perderá acceso a <strong>${u.tenant_name}</strong>.<br><span style="font-size:11px;color:#94a3b8">No se elimina de Supabase Auth.</span></p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, remover', cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: '#ef4444',
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from('saas_tenant_users').delete().eq('id', u.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setUsers((prev) => prev.filter((x) => x.id !== u.id));
    Swal.fire({ title: 'Usuario removido del tenant', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  // ── Assign / save from modal ──────────────────────────────────────────────────
  const handleSaveAssign = async (data: { user_id: string; tenant_id: string; role: string }) => {
    if (isNew) {
      const { data: created, error } = await supabase
        .from('saas_tenant_users')
        .insert({ user_id: data.user_id, tenant_id: data.tenant_id, role: data.role })
        .select().maybeSingle();
      if (error) {
        Swal.fire({
          title: 'Error',
          text: error.code === '23505' ? 'Este usuario ya está asignado a ese tenant.' : error.message,
          icon: 'error', ...SW,
        });
        return;
      }
      if (created) {
        const tenant = tenants.find((t) => t.id === data.tenant_id);
        setUsers((prev) => [
          { ...(created as TenantUser), tenant_name: tenant?.name ?? '—', is_super_admin: superAdminIds.has(data.user_id) },
          ...prev,
        ]);
        Swal.fire({ title: 'Usuario asignado al tenant', icon: 'success', timer: 1500, showConfirmButton: false, ...SW });
      }
    } else {
      if (!selectedUser) return;
      const { error } = await supabase.from('saas_tenant_users').update({ role: data.role }).eq('id', selectedUser.id);
      if (error) { Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW }); return; }
      setUsers((prev) => prev.map((u) => u.id === selectedUser.id ? { ...u, role: data.role } : u));
      Swal.fire({ title: 'Rol actualizado', icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
    }
    setModalOpen(false);
  };

  // ── Stats ─────────────────────────────────────────────────────────────────────
  const stats = [
    { label: 'Asignaciones',   value: users.length,                                    icon: 'ri-links-line',       color: 'text-blue-600 bg-blue-50' },
    { label: 'Usuarios únicos',value: uniqueUserIds.size,                               icon: 'ri-user-line',        color: 'text-slate-700 bg-slate-100' },
    { label: 'Owners',         value: users.filter((u) => u.role === 'owner').length,   icon: 'ri-vip-crown-line',   color: 'text-amber-700 bg-amber-50' },
    { label: 'Super Admins',   value: superAdmins.length,                               icon: 'ri-shield-star-line', color: 'text-violet-700 bg-violet-50' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <SuperAdminLayout>
      <SAHeader
        title="Users"
        subtitle="Usuarios de la plataforma — asignaciones y permisos"
        actions={
          <button
            onClick={() => { setSelectedUser(null); setIsNew(true); setModalOpen(true); }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line"></i></span>
            Asignar Usuario
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${s.color.split(' ')[1]}`}>
                <i className={`${s.icon} text-base ${s.color.split(' ')[0]}`}></i>
              </span>
              <div>
                <p className="text-slate-400 text-xs">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color.split(' ')[0]}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-5">
          {([
            { id: 'assignments', label: 'Tenant Assignments', icon: 'ri-building-2-line', count: users.length },
            { id: 'superadmins', label: 'Super Admins', icon: 'ri-shield-star-line', count: superAdmins.length },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap cursor-pointer border-b-2 -mb-px transition-all ${
                viewTab === tab.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></span>
              {tab.label}
              <span className={`min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${
                viewTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por user_id o tenant…"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-400 transition-colors"
            />
          </div>

          {viewTab === 'assignments' && (
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
              {['all', ...ROLES].map((r) => (
                <button
                  key={r}
                  onClick={() => setRoleFilter(r)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                    roleFilter === r ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {r === 'all' ? 'Todos' : r}
                </button>
              ))}
            </div>
          )}

          <span className="text-slate-400 text-sm ml-auto whitespace-nowrap">
            {viewTab === 'assignments' ? `${filteredUsers.length} asignaciones` : `${filteredSA.length} super admins`}
          </span>
        </div>

        {loading ? <LoadingState /> : (
          <>
            {/* ── TAB: Assignments ── */}
            {viewTab === 'assignments' && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px]">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {['User ID', 'Email', 'Tenant', 'Rol', 'SA', 'Asignado', 'Acciones'].map((h) => (
                          <th key={h} className={`text-slate-400 text-xs font-semibold uppercase tracking-wider py-3.5 ${h === 'Acciones' ? 'text-right pr-5' : 'text-left px-5'}`}>
                            {h === 'Email' ? (
                              <span className="flex items-center gap-1">Email <span className="text-slate-300 normal-case font-normal">(Auth)</span></span>
                            ) : h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredUsers.map((u) => {
                        const rs   = ROLE_STYLES[u.role] ?? ROLE_STYLES.member;
                        const isSA = superAdminIds.has(u.user_id);
                        const tenant = tenants.find((t) => t.id === u.tenant_id);
                        const planBadge = PLAN_BADGE[tenant?.plan_code ?? 'free'] ?? PLAN_BADGE.free;

                        return (
                          <tr key={u.id} className="hover:bg-slate-50/60 transition-colors">

                            {/* User ID */}
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 text-xs font-bold ${isSA ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                                  {isSA ? <i className="ri-shield-star-fill text-sm"></i> : <i className="ri-user-line text-sm"></i>}
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-slate-600 text-xs font-mono">
                                      {u.user_id.slice(0, 8)}…{u.user_id.slice(-6)}
                                    </p>
                                    <button
                                      onClick={() => copyToClipboard(u.user_id)}
                                      className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-blue-500 transition-colors cursor-pointer"
                                      title="Copiar User ID completo"
                                    >
                                      <i className="ri-file-copy-line text-xs"></i>
                                    </button>
                                  </div>
                                  {isSA && (
                                    <span className="text-violet-600 text-[9px] font-bold uppercase tracking-wider">Super Admin</span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Email — not available from client */}
                            <td className="px-5 py-3.5">
                              <span className="text-slate-300 text-xs italic">—</span>
                            </td>

                            {/* Tenant */}
                            <td className="px-5 py-3.5">
                              {tenant ? (
                                <div>
                                  <Link
                                    to={`/sa/tenants/${tenant.id}`}
                                    className="text-slate-800 text-sm font-medium hover:text-blue-600 transition-colors"
                                  >
                                    {tenant.name}
                                  </Link>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-slate-400 text-[10px] font-mono">{tenant.slug}</span>
                                    {tenant.plan_code && (
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${planBadge}`}>
                                        {tenant.plan_code}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-400 text-sm">—</span>
                              )}
                            </td>

                            {/* Role — clickable */}
                            <td className="px-5 py-3.5">
                              <button
                                onClick={() => handleChangeRole(u)}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize cursor-pointer hover:opacity-75 transition-opacity ${rs.bg} ${rs.text}`}
                                title="Clic para cambiar rol"
                              >
                                {u.role}
                              </button>
                            </td>

                            {/* SA toggle */}
                            <td className="px-5 py-3.5">
                              <button
                                onClick={() => handleToggleSA(u.user_id, u.tenant_name)}
                                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${isSA ? 'bg-violet-600' : 'bg-slate-200'}`}
                                title={isSA ? 'Quitar Super Admin' : 'Dar Super Admin'}
                              >
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isSA ? 'left-5' : 'left-0.5'}`} />
                              </button>
                            </td>

                            {/* Created at */}
                            <td className="px-5 py-3.5">
                              <p className="text-slate-400 text-xs whitespace-nowrap">
                                {new Date(u.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}
                              </p>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-3.5">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => { setSelectedUser(u); setIsNew(false); setModalOpen(true); }}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                                  title="Editar asignación"
                                >
                                  <i className="ri-edit-line text-sm"></i>
                                </button>
                                <button
                                  onClick={() => handleRemove(u)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                  title="Remover del tenant"
                                >
                                  <i className="ri-user-unfollow-line text-sm"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {filteredUsers.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-14 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-300">
                                <i className="ri-team-line text-xl"></i>
                              </div>
                              <p className="text-slate-400 text-sm">
                                {users.length === 0 ? 'Sin asignaciones — asigna el primer usuario' : 'Sin coincidencias para los filtros actuales'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer note */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2">
                  <i className="ri-information-line text-slate-400 text-sm"></i>
                  <p className="text-slate-400 text-xs">
                    Emails no disponibles desde el cliente — requieren Supabase Auth Admin API (service_role).
                    Búscalos en <strong>Supabase → Authentication → Users</strong>.
                  </p>
                  <span className="ml-auto text-slate-300 text-xs">Clic en rol para cambiar · Toggle para SA</span>
                </div>
              </div>
            )}

            {/* ── TAB: Super Admins ── */}
            {viewTab === 'superadmins' && (
              <div className="space-y-4">
                {/* Warning */}
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center text-violet-600 shrink-0 mt-0.5">
                    <i className="ri-shield-star-fill text-sm"></i>
                  </span>
                  <div>
                    <p className="text-violet-800 text-sm font-semibold">Super Admins — acceso total a la plataforma</p>
                    <p className="text-violet-600 text-xs mt-0.5 leading-relaxed">
                      Los Super Admins pueden ver y modificar todos los tenants, módulos, planes y configuración del SaaS.
                      Solo otorga este permiso a usuarios de confianza.
                    </p>
                  </div>
                </div>

                {/* SA Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-medium">Super Admins registrados</p>
                    <p className="text-violet-600 text-xs font-semibold">{superAdmins.length} total</p>
                  </div>

                  {filteredSA.length === 0 ? (
                    <div className="py-12 text-center">
                      <p className="text-slate-400 text-sm">Sin super admins registrados</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {filteredSA.map((sa) => {
                        const tenantAssignments = users.filter((u) => u.user_id === sa.user_id);
                        return (
                          <div key={sa.id || sa.user_id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors">
                            {/* Avatar */}
                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-violet-100 text-violet-700 shrink-0">
                              <i className="ri-shield-star-fill text-base"></i>
                            </div>

                            {/* User ID */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-slate-700 text-sm font-mono font-medium truncate">
                                  {sa.user_id}
                                </p>
                                <button
                                  onClick={() => copyToClipboard(sa.user_id)}
                                  className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-blue-500 transition-colors cursor-pointer shrink-0"
                                  title="Copiar User ID"
                                >
                                  <i className="ri-file-copy-line text-xs"></i>
                                </button>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-bold">
                                  <i className="ri-shield-star-fill text-[10px]"></i>
                                  Super Admin
                                </span>
                                {tenantAssignments.length > 0 && (
                                  <span className="text-slate-400 text-xs">
                                    {tenantAssignments.length} tenant{tenantAssignments.length !== 1 ? 's' : ''}:
                                    {tenantAssignments.map((t) => (
                                      <Link
                                        key={t.id}
                                        to={`/sa/tenants/${t.tenant_id}`}
                                        className="ml-1 text-blue-500 hover:text-blue-700 font-medium"
                                      >
                                        {t.tenant_name}
                                      </Link>
                                    ))}
                                  </span>
                                )}
                                {tenantAssignments.length === 0 && (
                                  <span className="text-slate-400 text-xs italic">Sin tenant asignado</span>
                                )}
                              </div>
                            </div>

                            {/* Added date */}
                            <div className="text-right shrink-0">
                              <p className="text-slate-400 text-xs">Agregado</p>
                              <p className="text-slate-500 text-xs font-medium">
                                {new Date(sa.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>

                            {/* Remove SA */}
                            <button
                              onClick={() => handleToggleSA(sa.user_id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                              title="Quitar Super Admin"
                            >
                              <i className="ri-user-unfollow-line text-sm"></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {saOnlyCount > 0 && (
                    <div className="px-5 py-3 border-t border-slate-100 bg-amber-50">
                      <p className="text-amber-600 text-xs">
                        <i className="ri-information-line mr-1"></i>
                        {saOnlyCount} super admin{saOnlyCount !== 1 ? 's' : ''} sin tenant asignado.
                        Usa "Asignar Usuario" para conectarlos a un tenant.
                      </p>
                    </div>
                  )}
                </div>

                {/* Add SA note */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-2.5">
                  <i className="ri-information-line text-slate-400 text-sm mt-0.5"></i>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    Para agregar un nuevo Super Admin, ve a la pestaña <strong className="text-slate-600">Tenant Assignments</strong>,
                    encuentra al usuario y activa el toggle <strong className="text-slate-600">SA</strong>.
                    O usa el botón "Asignar Usuario" para primero registrarlo en un tenant.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <AssignUserModal
          user={selectedUser}
          isNew={isNew}
          tenants={tenants}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveAssign}
        />
      )}
    </SuperAdminLayout>
  );
}
